/**
 * Verification — the signed-in user's self-service KYC/FICA state (web port).
 *
 * Phase-1 verification intent. Loads the caller's own KYC state via GET /kyc/me
 * and lets them register a verification intent at a FICA tier via POST /kyc.
 * Tier 1 ("cooperative-vouched") basic verification is the default and the only
 * one that does anything useful pre-partner-integration: the backend stub bumps
 * ficaStatus to 'submitted' and records the tier. Document upload + liveness
 * (Tier 2+, Smile ID / D-011) land in a later phase — this captures intent only.
 *
 * Contract verified against services/api/src/kyc/dto.ts + kyc.controller.ts via
 * the manual client src/api/manual/kyc.ts (validate-API-before-smoke rule).
 *
 * GET /kyc/me does not 404 in practice for a signed-in user (the service always
 * resolves the caller's user row, returning a default 'none' object), but we
 * still defensively catch a 404 in the queryFn and resolve to a synthetic
 * "not started" object so a brand-new account never sees a hard error page.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  ListItem,
  Stepper,
  Stack,
  StatusPill,
  Text,
  type StatusPillVariant,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { kycControllerGetMe, kycControllerSubmit } from '../api/generated/kyc/kyc';
import type { KycDto, KycDtoFicaStatus } from '../api/generated/models';

/** StatusPill presentation per FICA status. `variant` maps the FICA state onto
 *  the kit's semantic pill variants (mirroring the old MUI-Chip colour intent:
 *  info→due, success→active, error→overdue, warning→due, default→upcoming);
 *  `icon` is a Material Symbols ligature; `labelKey` is an i18n key resolved
 *  through t() at render (TD-088). Keyed by the orval-generated FICA type. */
const STATUS_PILL: Record<
  KycDtoFicaStatus,
  { labelKey: string; variant: StatusPillVariant; icon: string }
> = {
  none: { labelKey: 'verification.status.none', variant: 'upcoming', icon: 'radio_button_unchecked' },
  submitted: { labelKey: 'verification.status.submitted', variant: 'due', icon: 'schedule' },
  verified: { labelKey: 'verification.status.verified', variant: 'active', icon: 'check_circle' },
  rejected: { labelKey: 'verification.status.rejected', variant: 'overdue', icon: 'error' },
  expired: { labelKey: 'verification.status.expired', variant: 'due', icon: 'history' },
};

/** Synthetic "not started" state — used when no KYC row exists / GET 404s. */
const SYNTHETIC_NONE: KycDto = {
  userId: '',
  ficaStatus: 'none',
  ficaTier: null,
  livenessRef: null,
  docMinioKey: null,
  reviewedBy: null,
  reviewedAt: null,
  expiresAt: null,
};

/** Format an ISO timestamp for display; em-dash placeholder when absent. */
function formatTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** A simple label/value row used inside the status card, as a kit `ListItem`. */
function DetailRow({
  label,
  value,
  leadingIcon,
  noDivider = false,
}: {
  label: string;
  value: string;
  leadingIcon?: string;
  noDivider?: boolean;
}) {
  return (
    <ListItem
      leadingIcon={leadingIcon}
      title={
        <Text variant="body-medium" color="text.secondary">
          {label}
        </Text>
      }
      trailing={
        <Box sx={{ textAlign: 'right' }}>
          <Text variant="body-medium" color="text.primary">
            <Box component="span" sx={{ fontWeight: 500 }}>
              {value}
            </Box>
          </Text>
        </Box>
      }
      noDivider={noDivider}
    />
  );
}

export function VerificationScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const kycQuery = useQuery({
    queryKey: ['my-kyc'],
    // GET /kyc/me shouldn't 404 for a signed-in user, but if it does we treat
    // it as a clean "not started" state rather than an error page.
    queryFn: async ({ signal }): Promise<KycDto> => {
      try {
        return await kycControllerGetMe(signal);
      } catch (err) {
        if (err instanceof FseApiError && err.status === 404) {
          return SYNTHETIC_NONE;
        }
        throw err;
      }
    },
    // Don't retry — a 404 is handled inline and other errors should surface fast.
    retry: false,
  });

  const submitMutation = useMutation({
    mutationFn: () => kycControllerSubmit({ ficaTier: 1 }),
    onSuccess: (updated: KycDto) => {
      // Prime the cache so the status card reflects the new 'submitted' state.
      queryClient.setQueryData(['my-kyc'], updated);
    },
  });

  const kyc = kycQuery.data;
  const status = kyc?.ficaStatus ?? 'none';
  const alreadySubmitted = status === 'submitted';
  const alreadyVerified = status === 'verified';
  const submitDisabled =
    alreadySubmitted || alreadyVerified || submitMutation.isPending;

  // Active step in the three-stage journey stepper (none → submitted →
  // verified). rejected / expired reset the visual journey to the start.
  const journeyStep = alreadyVerified ? 2 : alreadySubmitted ? 1 : 0;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('verification.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('verification.subtitle')}
          </Text>
        </Box>
      </Box>

      {kycQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : kycQuery.isError || !kyc ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => kycQuery.refetch()}>
              {t('verification.retry')}
            </Button>
          }
        >
          {t('verification.loadError', { error: String(kycQuery.error) })}
        </Alert>
      ) : (
        <>
          {/* Status card */}
          <Card>
            <CardHeader title={t('verification.statusCardTitle')} />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <StatusPill variant={STATUS_PILL[status].variant} icon={STATUS_PILL[status].icon}>
                    {t(STATUS_PILL[status].labelKey)}
                  </StatusPill>
                </Box>

                {/* Verification journey — reuses the three status labels as
                    the step names (no new i18n keys). */}
                <Box sx={{ pt: 1 }}>
                  <Stepper
                    steps={[
                      t('verification.status.none'),
                      t('verification.status.submitted'),
                      t('verification.status.verified'),
                    ]}
                    activeStep={journeyStep}
                  />
                </Box>

                <Divider />
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <DetailRow
                    leadingIcon="workspace_premium"
                    label={t('verification.tier')}
                    value={kyc.ficaTier != null ? String(kyc.ficaTier) : '—'}
                  />
                  <DetailRow
                    leadingIcon="fact_check"
                    label={t('verification.reviewedAt')}
                    value={formatTs(kyc.reviewedAt)}
                  />
                  <DetailRow
                    leadingIcon="event_busy"
                    label={t('verification.expiresAt')}
                    value={formatTs(kyc.expiresAt)}
                    noDivider
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Submit / intent card */}
          <Card>
            <CardHeader title={t('verification.getVerified')} />
            <CardContent>
              <Stack spacing={2}>
                <Text variant="body-medium" color="text.secondary">
                  {t('verification.intentBody')}
                </Text>

                {submitMutation.isError ? (
                  <Alert severity="error">
                    {t('verification.submitError', { error: String(submitMutation.error) })}
                  </Alert>
                ) : null}

                {submitMutation.isSuccess ? (
                  <Alert severity="success">
                    {t('verification.submitSuccess')}
                  </Alert>
                ) : null}

                {alreadyVerified ? (
                  <Alert severity="success">{t('verification.alreadyVerified')}</Alert>
                ) : alreadySubmitted ? (
                  <Alert severity="info">{t('verification.alreadySubmitted')}</Alert>
                ) : (
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => submitMutation.mutate()}
                      disabled={submitDisabled}
                    >
                      {submitMutation.isPending
                        ? t('verification.submitting')
                        : t('verification.submitButton')}
                    </Button>
                  </Box>
                )}

                <Text variant="body-small" color="text.secondary">
                  {t('verification.tierNote')}
                </Text>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
